/* eslint-disable @next/next/no-img-element */
import React, { useContext, useEffect, useRef, useState } from "react";

import classNames from "classnames";
import Image from "next/legacy/image";
import styled from "styled-components";

import ComponentContext from "../../../contexts/ComponentContext";
import Markdown from "../../styles/Markdown";

const StyledContainer = styled.div`
  position: relative;
  opacity: ${({ shown }) => (shown ? "1" : "0")};
  transform: ${({ shown }) => (shown ? "translateY(0)" : "translateY(50%)")};
  transition: opacity 0.75s ease, transform 0.75s ease;
  display: flex;
  justify-content: center;
`;

const StyledWrapper = styled.div`
  ${(props) => props.dynamicStyles}
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
`;

const StyledImageContainer = styled.div`
  flex: 0 0 100%;
  max-width: 100%;
  padding: 0 15px;
  margin-bottom: 30px;

  @media (min-width: 481px) {
    flex: 0 0 50%; /* Two images per row */
    max-width: 50%;
  }

  @media (min-width: 769px) {
    flex: 0 0 ${({ columnWidth }) => columnWidth}%;
    max-width: ${({ columnWidth }) => columnWidth}%;
  }
`;

const StyledImage = styled.div`
  position: relative;
  width: 100%;
  height: 0;
  padding-bottom: ${({ aspectRatio }) => aspectRatio}%;
`;

function AnimatedScrollImage() {
  const { animatedItems, imagesInRow, CSS, uniqueId, wrapperClassName } =
    useContext(ComponentContext);

  const [isVisible, setIsVisible] = useState(false);
  const [isAlreadyVisible, setIsAlreadyVisible] = useState(false);
  const imageRef = useRef(null);

  const handleScroll = () => {
    if (isAlreadyVisible) return;

    const rect = imageRef.current.getBoundingClientRect();
    if (rect.top <= window.innerHeight && rect.bottom >= 0) {
      setIsVisible(true);
      setIsAlreadyVisible(true);
    } else {
      setIsVisible(false);
    }
  };

  const checkInitialVisibility = () => {
    const rect = imageRef.current.getBoundingClientRect();
    if (rect.top <= window.innerHeight && rect.bottom >= 0) {
      setIsVisible(true);
      setIsAlreadyVisible(true);
    }
  };

  useEffect(() => {
    checkInitialVisibility();
    window.addEventListener("scroll", handleScroll);
    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, []);

  const imagesRowMap = {
    one: 1,
    two: 2,
    three: 3,
    four: 4,
    five: 5,
    six: 6,
  };

  const columnWidth = 100 / (imagesRowMap[imagesInRow] || 1);

  return (
    <StyledWrapper
      className={classNames(wrapperClassName)}
      id={uniqueId}
      ref={imageRef}
      dynamicStyles={CSS}
    >
      {animatedItems?.map((boxLink) => (
        <StyledImageContainer key={boxLink.id} columnWidth={columnWidth}>
          <StyledContainer
            className="animated-scroll-image position-relative"
            shown={isVisible}
          >
            {boxLink?.image?.id ? (
              <StyledImage
                aspectRatio={
                  boxLink.image?.height && boxLink.image?.width
                    ? (boxLink.image.height / boxLink.image.width) * 100
                    : 75
                }
              >
                <Image
                  alt={boxLink.image?.alternativeText || "Image"}
                  src={boxLink.image?.url}
                  layout="fill"
                  objectFit="contain"
                />
              </StyledImage>
            ) : (
              <Markdown>{boxLink.text}</Markdown>
            )}
          </StyledContainer>
        </StyledImageContainer>
      ))}
    </StyledWrapper>
  );
}

export default AnimatedScrollImage;
